import { useState, useRef, useEffect } from 'react';
import { useMeals } from '@/hooks/useMeals';
import { MealCardDb } from './MealCardDb';
import { MealCustomizationModalDb } from './MealCustomizationModalDb';
import { DbMeal } from '@/types/database';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimateOnScroll } from './AnimateOnScroll';

export function MealsSlider() {
  const [customizingMeal, setCustomizingMeal] = useState<DbMeal | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  const { data: meals, isLoading, error } = useMeals();

  const checkScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScroll);
      return () => slider.removeEventListener('scroll', checkScroll);
    }
  }, [meals]);

  const scroll = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = 320;
      const newScrollLeft = direction === 'left' 
        ? sliderRef.current.scrollLeft - scrollAmount
        : sliderRef.current.scrollLeft + scrollAmount;
      
      sliderRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  if (isLoading) {
    return (
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </section>
    );
  }

  if (error || !meals?.length) {
    return null;
  }

  return (
    <section className="py-16 bg-secondary/30 overflow-hidden">
      <div className="container mx-auto px-4">
        <AnimateOnScroll animation="fade-in-right">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                המנות שלנו
              </h2>
              <p className="text-muted-foreground text-lg">
                גלו את כל המנות הטעימות שלנו
              </p>
            </div>
            
            {/* Navigation Arrows */}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                aria-label="הבא"
                className="rounded-full h-12 w-12 border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 disabled:opacity-30 hover:scale-110 hover:shadow-lg"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                aria-label="הקודם"
                className="rounded-full h-12 w-12 border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 disabled:opacity-30 hover:scale-110 hover:shadow-lg"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Slider Container */}
        <div className="relative">
          {/* Gradient Edges with Fade */}
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-secondary/30 via-secondary/20 to-transparent z-10 pointer-events-none" />
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-secondary/30 via-secondary/20 to-transparent z-10 pointer-events-none" />
          
          {/* Slider */}
          <div
            ref={sliderRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {meals.map((meal, index) => (
              <div
                key={meal.id}
                className="flex-shrink-0 w-[280px] md:w-[300px] opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${index * 80}ms`, animationFillMode: 'forwards' }}
              >
                <MealCardDb
                  meal={meal}
                  onCustomize={setCustomizingMeal}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Customization Modal */}
      {customizingMeal && (
        <MealCustomizationModalDb
          meal={customizingMeal}
          onClose={() => setCustomizingMeal(null)}
        />
      )}
    </section>
  );
}
